/**
 * Axios instance with MSAL authentication interceptor.
 *
 * Mirrors Angular's MsalInterceptor pattern:
 *  - A protectedResourceMap maps URL prefixes → OAuth scopes
 *  - A request interceptor silently acquires a token and attaches the
 *    Authorization header before every outgoing request
 *  - A response interceptor handles retries on transient failures
 *
 * Components and services call the simple httpGet / httpPost wrappers
 * exported from httpService.ts – no msalInstance prop-drilling needed.
 */
import axios from 'axios';
import { getMsalInstance } from './msalConfig';
import { environment } from '../environments/environment';
import { publishHttpError, trigger403 } from './httpErrorBus';

// ── Protected resource map (mirrors Angular MsalInterceptorConfigFactory) ──

const protectedResourceMap = new Map<string, string[]>([
  [environment.apiConfig.graph_api.uri, environment.apiConfig.graph_api.scopes],
  [environment.apiConfig.articleplan_api.uri, environment.apiConfig.articleplan_api.scopes],
]);

function getScopesForUrl(url: string): string[] | undefined {
  for (const [prefix, scopes] of protectedResourceMap) {
    if (url.startsWith(prefix)) return scopes;
  }
  return undefined;
}

// ── Axios instance ─────────────────────────────────────────────────

const axiosInstance = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor – attach Bearer token ──────────────────────

axiosInstance.interceptors.request.use(async (config) => {
  const url = config.url ?? '';
  const scopes = getScopesForUrl(url);
  if (!scopes) return config;

  const msalInstance = getMsalInstance();
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];
  if (!account) return config;

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account });
    if (result.accessToken) {
      config.headers.Authorization = `Bearer ${result.accessToken}`;
    }
  } catch {
    // Silent token acquisition failed – proceed without token.
    // A 401 response will be caught by the response interceptor or caller.
  }
  return config;
});

// ── Response interceptor – retry on transient errors ───────────────

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 2000;
const RATE_LIMIT_MESSAGE = 'Too many requests — please wait before trying again.';
const DEFAULT_RATE_LIMIT_SECONDS = 60;

function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data.length > 0) return data;
    if (typeof data?.message === 'string') return data.message as string;
    if (typeof data?.title === 'string') return data.title as string;
    return error.message;
  }
  return error instanceof Error ? error.message : 'Unknown error';
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) throw error;

    // Initialize retry counter
    config.__retryCount = config.__retryCount ?? 0;

    const status = error.response?.status;

    // 403 → trigger error page, no toast, no retry
    if (status === 403) {
      trigger403();
      throw error;
    }

    // 429 responses must not be retried because the server has explicitly asked the client to wait.
    if (status === 429) {
      const retryAfter = Number(error.response?.headers?.['retry-after']);
      const retryAfterSeconds = Number.isFinite(retryAfter) && retryAfter > 0
        ? Math.ceil(retryAfter)
        : DEFAULT_RATE_LIMIT_SECONDS;
      publishHttpError(RATE_LIMIT_MESSAGE, retryAfterSeconds * 1000);
      throw error;
    }

    // 500 → publish toast, no retry
    if (status === 500) {
      publishHttpError(extractErrorMessage(error));
      throw error;
    }

    if (config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      return axiosInstance(config);
    }

    // All retries exhausted → publish toast
    publishHttpError(extractErrorMessage(error));
    throw error;
  },
);

export default axiosInstance;
